import {
  CloseOutlined,
  PlusOutlined,
  SyncOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useIsMutating } from '@tanstack/react-query';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { booleanOptions, numberRegEx, questionCriteriaValueOptions } from '@app/constants';
import { QuestionOption } from '@app/utils';
import { Namespace, useICDCodesList, usePermissions } from '@data/index';
import {
  AnswerType,
  CriteriaType,
  QuestionCriterionView,
  TrialCriteriaView,
} from '@data/sdk/match';
import {
  Button,
  AntdCol,
  AntdForm,
  AntdInput,
  AntdRadio,
  AntdRow,
  AntdSelect,
  AntdSpace,
  AntdTypography,
  AntdDrawer,
  AntdCheckbox,
  AntdTooltip,
  AntdAlert,
} from '@ui/components';
import { ModifyMode, useEditCriteria, useFormData } from './hooks';

const { Text } = AntdTypography;
const { TextArea } = AntdInput;

type EditCriteriaProps = {
  criteria:
    | TrialCriteriaView
    | (QuestionCriterionView & {
        answerType: AnswerType;
        questionId: string;
        questionText: string;
      });
  invalidateQuestionCriteria?: () => void;
  isAdmin: boolean;
  isOpen: boolean;
  isQuestionCriteria?: boolean;
  onClose: () => void;
  trialId: string;
};

export const EditCriteria = ({
  isOpen,
  onClose,
  criteria,
  invalidateQuestionCriteria,
  isQuestionCriteria = false,
  trialId,
}: EditCriteriaProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);

  const isMutating = !!useIsMutating();
  const { IS_REVIEWER } = usePermissions();

  const {
    form,
    question,
    questionText,
    answerType,
    mode,
    autoTranslate,
    initialValues,
    isICDCodeCriteria,
    isSharedStandardQuestion,
  } = useFormData(criteria);
  const { icdCodesOptions, fetchNextPage, isLoading, handleSearchChange } = useICDCodesList();
  const {
    searchText,
    selectOptions,
    questionOptions,
    questionsLoading,
    showSuggestedList,
    createCustomQuestion,
    setSearchText,
    handleClose,
    handleSubmit,
    handleModeChange,
    handleClearSelect,
    handleSelectQuestion,
    handleReplaceQuestion,
    handleAnswerTypeChange,
    toggleShowSuggestedQuestions,
  } = useEditCriteria({
    initialValues,
    form,
    onClose,
    isQuestionCriteria,
    trialId,
    criteria,
    invalidateQuestionCriteria,
  });

  const handleScroll = (e: any) => {
    if (e.target.scrollTop + e.target.offsetHeight + 2 > e.target.scrollHeight) {
      fetchNextPage();
    }
  };

  return (
    <AntdDrawer
      closeIcon={null}
      onClose={handleClose}
      open={isOpen}
      title={
        <Text className="font-medium text-xl">
          {t('trial_page.edit_trial_criteria_and_linked_question')}
        </Text>
      }
      size={600}
      extra={<CloseOutlined onClick={handleClose} className="text-base-700" />}
      styles={{
        header: { borderBottom: 'none' },
        body: { paddingTop: 0 },
      }}
      destroyOnHidden
    >
      <AntdForm
        form={form}
        layout="vertical"
        autoComplete="off"
        requiredMark={false}
        onFinish={handleSubmit}
        className="[&>div]:mb-4"
        scrollToFirstError
        initialValues={initialValues}
      >
        <Text className="text-base-500 block mb-6">
          {t('trial_page.edit_criteria_description')}
        </Text>
        <Text className="font-semibold text-base text-base-900 block mb-3">
          {t('trial_page.trial_criteria')}
        </Text>

        <AntdForm.Item name="type" className="mb-0" label={t('trial_page.type')}>
          <AntdRadio.Group disabled>
            <AntdRadio value={CriteriaType.Inclusion}>{t('trial_page.inclusion')}</AntdRadio>
            <AntdRadio value={CriteriaType.Exclusion}>{t('trial_page.exclusion')}</AntdRadio>
          </AntdRadio.Group>
        </AntdForm.Item>

        <AntdForm.Item
          name="originalCriteriaText"
          rules={[{ required: true, message: t('patient:validations.required') }]}
          className="mb-0 flex-1"
          label={t('trial_page.criteria')}
        >
          <TextArea rows={5} className="w-full rounded-default" style={{ resize: 'none' }} />
        </AntdForm.Item>

        <Text className="font-semibold text-base text-base-900 block mb-3">
          {t('trial_page.linked_question')}
        </Text>
        {!isICDCodeCriteria && (
          <Text className="text-sm block mb-3">
            {t('trial_page.how_would_you_like_to_proceed')}
          </Text>
        )}

        {isSharedStandardQuestion && !isICDCodeCriteria && (
          <AntdAlert
            showIcon
            type="warning"
            className="mb-3"
            title={t('trial_page.shared_standard_question_warning')}
          />
        )}

        <AntdForm.Item name="mode" className={cx(isICDCodeCriteria && 'hidden')}>
          <AntdRadio.Group value={mode} onChange={(e) => handleModeChange(e.target.value)}>
            <AntdRadio
              value={ModifyMode.ModifyExisting}
              disabled={isSharedStandardQuestion}
              className={cx(
                'w-full border-2 border-solid rounded-default p-4 border-primary-200 mt-2 radio',
                mode === ModifyMode.ModifyExisting && '!border-primary-600',
              )}
            >
              <div className="flex flex-col">
                <Text
                  className={
                    mode === ModifyMode.ModifyExisting ? 'font-bold' : 'font-bold text-base-600'
                  }
                >
                  {t('trial_page.modify_existing_question_title')}
                  <AntdTooltip title={t('trial_page.modify_existing_question_tooltip')}>
                    <InfoCircleOutlined className="ml-1 text-base-500" />
                  </AntdTooltip>
                </Text>
                <Text className="text-sm text-base-500">
                  {t('trial_page.modify_existing_question_description')}
                </Text>
              </div>
            </AntdRadio>
            <AntdRadio
              value={ModifyMode.CreateNew}
              className={cx(
                'w-full border-2 border-solid rounded-default p-4 border-primary-200 mt-2 radio',
                mode === ModifyMode.CreateNew && '!border-primary-600',
              )}
            >
              <div className="flex flex-col">
                <Text
                  className={
                    mode === ModifyMode.CreateNew ? 'font-bold' : 'font-bold text-base-600'
                  }
                >
                  {t('trial_page.create_new_question_title')}
                </Text>
                <Text className="text-sm text-base-500">
                  {t('trial_page.create_new_question_description')}
                </Text>
              </div>
            </AntdRadio>
          </AntdRadio.Group>
        </AntdForm.Item>
        {mode === ModifyMode.ModifyExisting && (
          <AntdForm.Item
            name="questionText"
            label={
              isICDCodeCriteria ? (
                t('trial_page.question')
              ) : (
                <Text className="text-base-600">
                  {t('trial_page.modify_existing_question_title')}
                  <AntdTooltip title={t('trial_page.modify_existing_question_warning')}>
                    <InfoCircleOutlined className="ml-1 text-base-500" />
                  </AntdTooltip>
                </Text>
              )
            }
          >
            <AntdInput disabled={isICDCodeCriteria || isSharedStandardQuestion} />
          </AntdForm.Item>
        )}
        {mode === ModifyMode.CreateNew && !isICDCodeCriteria && (
          <AntdForm.Item
            name="question"
            label={
              <Text className="text-base-600">
                {t('trial_page.create_new_question_title')}
                <AntdTooltip title={t('trial_page.create_new_question_helper_description')}>
                  <InfoCircleOutlined className="ml-1 text-base-500" />
                </AntdTooltip>
              </Text>
            }
            rules={[{ required: true, message: t('patient:validations.required') }]}
          >
            <AntdSelect<unknown, QuestionOption>
              showSearch
              value={selectOptions?.find((el) => el?.value === question?.value) ?? searchText}
              placeholder={t('patient:common.type_here')}
              filterOption={false}
              onSearch={setSearchText}
              notFoundContent={null}
              loading={questionsLoading}
              allowClear
              styles={{ popup: { root: { padding: '0 0 4px' } } }}
              listHeight={500}
              onSelect={(_, option) => handleSelectQuestion(option)}
              onClear={handleClearSelect}
              optionLabelProp="label"
              disabled={!!initialValues.otherValues}
            >
              {selectOptions.map((el) => (
                <AntdSelect.Option
                  className={`${el.info ? 'text-base-600 bg-base-200' : 'mx-1'} ${
                    el.createOwn ? 'text-primary-600' : ''
                  }`}
                  {...el}
                  key={el.value}
                  disabled={el.info}
                >
                  {el.createOwn ? (
                    <>
                      <PlusOutlined /> {t('patient:common.add')} {`"${el.label}"`}
                    </>
                  ) : (
                    el.label
                  )}
                </AntdSelect.Option>
              ))}
            </AntdSelect>
            {showSuggestedList && (
              <AntdRow>
                <AntdSpace
                  orientation="vertical"
                  size={12}
                  className="w-full mt-1 p-4 rounded-lg bg-primary-200 "
                >
                  <Text>{t('trial_page.check_if_there_question_below')}:</Text>
                  <ul className="p-0 m-0">
                    {questionOptions.slice(0, 3).map((el) => (
                      <li
                        key={el.value}
                        className="list-none p-0 py-[1px] flex justify-between items-center border-solid border-0 border-t [&:last-child]:border-b border-base-300"
                      >
                        <Text className="font-medium">{el.label}</Text>{' '}
                        <Button
                          icon={<SyncOutlined />}
                          className="text-primary-600 px-0"
                          type="text"
                          onClick={() => handleReplaceQuestion(el)}
                        >
                          {t('patient:common.replace')}
                        </Button>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="bg-transparent font-medium shadow-none"
                    onClick={toggleShowSuggestedQuestions}
                  >
                    {t('trial_page.ignore_this_list')}
                  </Button>
                </AntdSpace>
              </AntdRow>
            )}
          </AntdForm.Item>
        )}

        {(mode === ModifyMode.ModifyExisting || (mode === ModifyMode.CreateNew && question)) && (
          <>
            <AntdRow justify="start" className="gap-6">
              {!initialValues.otherValues && (
                <AntdCol xs={10}>
                  <AntdForm.Item
                    name="answerType"
                    label={t('trial_page.value')}
                    rules={[{ required: true, message: t('patient:validations.required') }]}
                    className="mb-0"
                  >
                    <AntdSelect
                      options={questionCriteriaValueOptions()}
                      className="max-w-[240px]"
                      onChange={() => handleAnswerTypeChange()}
                      disabled={mode === ModifyMode.ModifyExisting || !createCustomQuestion}
                    />
                  </AntdForm.Item>
                </AntdCol>
              )}

              {/* ICD code criteria */}
              {initialValues.otherValues && (
                <AntdCol xs={12}>
                  <AntdForm.Item
                    name="otherValues"
                    label={t('trial_page.expected_answer')}
                    rules={[{ required: true, message: t('patient:validations.please_select') }]}
                  >
                    <AntdSelect
                      mode="multiple"
                      showSearch
                      options={icdCodesOptions}
                      filterOption={false}
                      loading={isLoading}
                      onPopupScroll={handleScroll}
                      onSearch={handleSearchChange}
                    />
                  </AntdForm.Item>
                </AntdCol>
              )}
              {answerType === AnswerType.Boolean && (
                <AntdCol xs={10}>
                  <AntdForm.Item
                    name="value"
                    label={t('trial_page.expected_answer')}
                    rules={[
                      {
                        required: answerType === AnswerType.Boolean,
                        message: t('patient:validations.please_select'),
                      },
                    ]}
                  >
                    <AntdSelect options={booleanOptions()} />
                  </AntdForm.Item>
                </AntdCol>
              )}
            </AntdRow>
            {answerType === AnswerType.Number && (
              <AntdRow justify="space-between" className="flex gap-6 flex-nowrap">
                <AntdCol>
                  <AntdForm.Item
                    name="minThreshold"
                    dependencies={['maxThreshold']}
                    label={t('trial_page.min_threshold')}
                    rules={[
                      { pattern: numberRegEx, message: t('patient:validations.should_be_number') },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value && !getFieldValue('maxThreshold')) {
                            return Promise.reject(new Error(t('patient:validations.required')));
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <AntdInput />
                  </AntdForm.Item>
                </AntdCol>
                <AntdCol>
                  <AntdForm.Item
                    name="maxThreshold"
                    dependencies={['minThreshold']}
                    label={t('trial_page.max_threshold')}
                    rules={[
                      { pattern: numberRegEx, message: t('patient:validations.should_be_number') },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value && !getFieldValue('minThreshold')) {
                            return Promise.reject(new Error(t('patient:validations.required')));
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <AntdInput />
                  </AntdForm.Item>
                </AntdCol>
                <AntdCol>
                  <AntdForm.Item name="unit" label={t('trial_page.unit')}>
                    <AntdInput disabled={!createCustomQuestion} />
                  </AntdForm.Item>
                </AntdCol>
              </AntdRow>
            )}
            {mode === ModifyMode.ModifyExisting &&
              !isICDCodeCriteria &&
              initialValues.questionText !== questionText && (
                <>
                  <AntdForm.Item name="autoTranslate" valuePropName="checked" className="mb-2 mt-2">
                    <AntdCheckbox>
                      {t('trial_page.auto_translate_checkbox')}
                      <AntdTooltip title={t('trial_page.auto_translate_tooltip')} className="ml-1">
                        <QuestionCircleOutlined className="text-base-500" />
                      </AntdTooltip>
                    </AntdCheckbox>
                  </AntdForm.Item>
                  {!autoTranslate && (
                    <AntdAlert
                      showIcon
                      title={t('trial_page.auto_translate_warning_unchecked')}
                      type="warning"
                    />
                  )}
                </>
              )}
          </>
        )}

        {!IS_REVIEWER && (
          <AntdSpace orientation="horizontal" className="w-full mt-2">
            <Button type="primary" htmlType="submit" loading={isMutating}>
              {t('trial_page.save')}
            </Button>
            <Button onClick={handleClose} disabled={isMutating}>
              {t('patient:common.cancel')}
            </Button>
          </AntdSpace>
        )}
      </AntdForm>
    </AntdDrawer>
  );
};
